// SAA-only email senders: confirmation (aanvraagbevestiging) and review (aanvraag onder handmatige
// beoordeling). Reuses core read-only for data extraction, attachment upload and the BFF send, but
// renders the body with the SAA template so the design lives entirely in the SAA app and never
// touches sluitstraat-core (Frisia keeps core's templates).

import {
  extractEmailDataFromWorkflow,
  uploadPublicFileToBlob,
  toGuidOrUndefined,
  sendEmail,
  sendEmailDeferred,
  getEnv,
  type RequestEmailData,
  type EmailAttachment,
  type EmailConfirmationLabels,
  type SendEmailResponse,
  type SluitstraatConfig,
} from '@sluitstraat/core';
import {
  buildSaaConfirmationEmailHtml,
  buildSaaReviewEmailHtml,
  type SaaConfirmationViewModel,
  type SaaEmailLine,
  type SaaReviewData,
} from './saaConfirmationTemplate';
import { resolveBrand } from './saaEmailTheme';
import { EMAIL_COPY, getEmailCopy, type Lang, type ProductId } from './saaEmailCopy';
import { resolveExtras, resolveOptions, type EmailTranslate } from './saaEmailCoverages';
import { getProductOrder } from '../workflowConfig';
import { SAA_APPLICATION_PDF_TEMPLATE, saaApplicationPdfFileName } from '../../../shared/applicationPdf';

type Translate = EmailTranslate;

const ICON_TO_PRODUCT: Record<string, ProductId> = {
  shield: 'aansprakelijkheid',
  house: 'woonhuis',
  package: 'inboedel',
  car: 'personenauto',
  scales: 'rechtsbijstand',
  firstaid: 'ongevallen',
  calendar: 'doorlopendereis',
};

// Which Contractdocument.PP section identifies each product (mirrors core.findProductWorkflowData).
const PRODUCT_PP_MATCH: Record<ProductId, (pp: any) => boolean> = {
  woonhuis: (pp) => pp.OP && !pp.IP && !pp.DA,
  inboedel: (pp) => !!pp.IP,
  aansprakelijkheid: (pp) => !!pp.DA,
  personenauto: (pp) => !!pp.OB,
  rechtsbijstand: (pp) => pp.WC && !pp.OB && !pp.IP && !pp.OP && !pp.DA,
  ongevallen: (pp) => pp.OD && !pp.DR,
  doorlopendereis: (pp) => !!pp.DR,
};

const findProductData = (workflowData: any, id: ProductId): any => {
  const direct = workflowData?.[id];
  if (direct?.Contractdocument?.PP) return direct;
  const match = PRODUCT_PP_MATCH[id];
  for (const key of Object.keys(workflowData || {})) {
    const pp = workflowData[key]?.Contractdocument?.PP;
    if (pp && match(pp)) return workflowData[key];
  }
  return null;
};

const HOOFDDEKKING_LABEL_KEY: Record<string, string> = {
  WA: 'screens.personenauto.coverage.wa',
  'WA+': 'screens.personenauto.coverage.waPlus',
  'WA+volledig': 'screens.personenauto.coverage.waFull',
};

const autoBaseLabel = (workflowData: any, t: Translate, lang: Lang): string => {
  const hoofddekking = findProductData(workflowData, 'personenauto')?.Supportsection?.Dekkingen?.Hoofddekking || 'WA';
  const labelKey = HOOFDDEKKING_LABEL_KEY[hoofddekking] ?? HOOFDDEKKING_LABEL_KEY.WA;
  return t(labelKey, { lng: lang });
};

// Dutch-formatted amount ("1.234,56") -> number; used only to decide whether a fee row is shown.
const parseDutchAmount = (value: string | undefined): number => {
  if (!value) return 0;
  const n = parseFloat(value.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const resolveLang = (config: SluitstraatConfig): Lang => {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('sluitstraat-language') : null;
  if (stored === 'nl' || stored === 'en') return stored;
  return (config.branding?.language as Lang) || 'nl';
};

const resolveBcc = (config: SluitstraatConfig): string | undefined => {
  // Mirror core's resolveBccEmail priority: the VITE_EMAIL_BCC env override BCCs the tester in
  // local/dev/test, where the TO is dummy data; without it the mail never reaches the tester's inbox.
  const envBcc = getEnv('VITE_EMAIL_BCC');
  if (envBcc) return envBcc;
  const bcc = (config as any).email?.bccEmail;
  if (Array.isArray(bcc)) return bcc.filter(Boolean).join(',') || undefined;
  return typeof bcc === 'string' ? bcc : undefined;
};

const buildViewModel = (
  emailData: RequestEmailData,
  workflowData: any,
  voornaam: string,
  lang: Lang,
  t: Translate
): SaaConfirmationViewModel => {
  const c = EMAIL_COPY[lang];
  const idOf = (iconKey?: string) => (iconKey ? ICON_TO_PRODUCT[iconKey] : undefined);

  const products = emailData.products.map((p) => {
    const id = idOf(p.iconKey);
    const names = id ? c.products[id] : undefined;
    const naam = names?.name ?? p.name;
    // Auto's base line is its coverage variant; other products use a fixed base label (or none).
    const base = id === 'personenauto' ? autoBaseLabel(workflowData, t, lang) : names?.base ?? '';
    // Products without a fixed base label (aansprakelijkheid, ongevallen, doorlopende reis) show
    // core's data-backed dekkingsregels instead (verzekerde som, verzekerde bedragen,
    // dekkingsgebied), so every product gets its dekking under the name. Products WITH a base
    // label skip them: for auto they would duplicate the variant and add kenteken/merk noise.
    // Every product then lists its chosen options (eigen risico) and selected extra coverages.
    const productData = id ? findProductData(workflowData, id) : null;
    const specs = [...(base ? [] : p.coverageDetails ?? []), ...resolveOptions(productData, id, t, lang)];
    return { naam, base, specs, extras: resolveExtras(productData, id, t, lang) };
  });

  const premieRegels: SaaEmailLine[] = emailData.products.map((p) => {
    const id = idOf(p.iconKey);
    return { label: id ? c.products[id].name : p.name, value: `€ ${p.premium}` };
  });
  // Fee order mirrors the on-screen PremiumBreakdownCard: Incassokosten (prolongation) first,
  // then Serviceabonnement (subscription).
  if (parseDutchAmount(emailData.prolongationFee) > 0) {
    premieRegels.push({ label: c.feeProlongation, value: `€ ${emailData.prolongationFee}` });
  }
  if (parseDutchAmount(emailData.subscriptionFee) > 0) {
    premieRegels.push({ label: c.feeSubscription, value: `€ ${emailData.subscriptionFee}` });
  }

  return {
    lang,
    voornaam: voornaam || emailData.customerName,
    startDate: emailData.startDate,
    products,
    premieRegels,
    totalePremie: emailData.totalPremium,
    betaalwijze: emailData.premiumPeriod === 'year' ? c.paymentYearly : c.paymentMonthly,
    klant: {
      naam: emailData.customerName,
      adres: emailData.customerAddress,
      geboortedatum: emailData.customerDob,
      email: emailData.customerEmail,
      telefoon: emailData.customerPhone,
    },
    contact: {
      telefoon: emailData.contactPhone,
      telefoonRaw: (emailData.contactPhone || '').replace(/[^0-9+]/g, ''),
      email: emailData.contactEmail,
      openingstijden: c.contactOpeningstijdenValue,
    },
  };
};

const buildGeneralTermsAttachment = async (config: SluitstraatConfig, lang: Lang): Promise<EmailAttachment | null> => {
  const terms = (config as any).email?.attachments?.generalTerms;
  if (!terms?.enabled) return null;
  const variant = terms[lang] || terms;
  const path = variant.path as string | undefined;
  const fileName = (variant.fileName as string | undefined) || 'Algemene Voorwaarden.pdf';
  if (!path) return null;
  try {
    return await uploadPublicFileToBlob(path, fileName, 'application/pdf');
  } catch (error) {
    console.error('[SAA email] General terms attachment failed, sending without it:', error);
    return null;
  }
};

// UI-level discounts (applyToData === false) must be mirrored into the email amounts, matching core's
// sendRequestConfirmationEmail. When applyToData is true the workflow data is already discounted.
const buildDiscountConfig = (config: SluitstraatConfig) => {
  const c = config as any;
  const out: {
    woonhuis?: { percentage: number };
    inboedel?: { percentage: number; excludeCoverages?: string[] };
    avp?: { percentage: number };
  } = {};
  if (c.woonhuisDiscount?.enabled && c.woonhuisDiscount.percentage > 0 && c.woonhuisDiscount.applyToData === false) {
    out.woonhuis = { percentage: c.woonhuisDiscount.percentage };
  }
  if (c.inboedelDiscount?.enabled && c.inboedelDiscount.percentage > 0 && c.inboedelDiscount.applyToData === false) {
    out.inboedel = { percentage: c.inboedelDiscount.percentage, excludeCoverages: c.inboedelDiscount.excludeCoverages || ['GP'] };
  }
  if (c.avpDiscount?.enabled && c.avpDiscount.percentage > 0 && c.avpDiscount.applyToData === false) {
    out.avp = { percentage: c.avpDiscount.percentage };
  }
  return out;
};

// "Ja"-answered acceptance questions (with toelichting) + accepted declaration labels for the
// review email. Mirrors core's buildEmailExtrasFromWorkflow, but filtered to yes-answers only —
// they are the reason the request is under review; the full J/N list stays in the internal mail.
const buildReviewData = (
  workflowData: any,
  paymentData: any,
  questionTexts: Record<string, string> | undefined,
  confirmationLabels: EmailConfirmationLabels | undefined
): SaaReviewData => {
  const acceptance = workflowData?.['acceptance-questions'] || {};
  const answers: Record<string, unknown> = acceptance.answers || {};
  const explanations: Record<string, string> = acceptance.explanations || {};
  const vragen = Object.entries(answers)
    .filter(([, answer]) => answer === 'J')
    .map(([id]) => ({ vraag: questionTexts?.[id] || id, toelichting: explanations[id] || undefined }));
  const paymentInfo = paymentData?.paymentInfo || {};
  const akkoorden = [
    confirmationLabels?.terms && paymentInfo.acceptTerms ? confirmationLabels.terms : null,
    confirmationLabels?.privacy && paymentInfo.privacyAccepted ? confirmationLabels.privacy : null,
    confirmationLabels?.sepa && paymentInfo.sepaAccepted ? confirmationLabels.sepa : null,
  ].filter((x): x is string => !!x);
  return { vragen, akkoorden };
};

export async function sendSaaConfirmationEmail(
  workflowData: any,
  paymentData: any,
  requestId: string,
  config: SluitstraatConfig,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  t: Translate
): Promise<SendEmailResponse> {
  return sendSaaEmail(workflowData, paymentData, requestId, config, selectedProducts, t);
}

// Review variant (aanvraag onder handmatige beoordeling): same design + data as the confirmation,
// review copy, plus the yes-answered vragen + akkoordverklaringen. Signature mirrors core's
// sendRequestReviewEmail (with `t` appended) so EndPageScreen only swaps the function name.
export async function sendSaaReviewEmail(
  workflowData: any,
  paymentData: any,
  requestId: string,
  config: SluitstraatConfig,
  questionTexts: Record<string, string> | undefined,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  confirmationLabels: EmailConfirmationLabels | undefined,
  t: Translate
): Promise<SendEmailResponse> {
  const review = buildReviewData(workflowData, paymentData, questionTexts, confirmationLabels);
  return sendSaaEmail(workflowData, paymentData, requestId, config, selectedProducts, t, review);
}

// Shared send path; presence of `review` switches template variant + subject.
async function sendSaaEmail(
  workflowData: any,
  paymentData: any,
  requestId: string,
  config: SluitstraatConfig,
  selectedProducts: Record<string, boolean | undefined> | undefined,
  t: Translate,
  review?: SaaReviewData
): Promise<SendEmailResponse> {
  const clientInfo = workflowData?.['client-information']?.clientInfo;
  if (!clientInfo?.email) {
    throw new Error('Client email is missing from workflow data');
  }

  const lang = resolveLang(config);
  const emailData = extractEmailDataFromWorkflow(workflowData, paymentData, config, lang, getProductOrder(), buildDiscountConfig(config), selectedProducts);
  emailData.requestId = requestId;

  // The workflow has no real first name (only initials like "T."), which reads oddly as "Beste T.,";
  // fall back to the full formal name (handled in buildViewModel) when no first name is present.
  const vm = buildViewModel(emailData, workflowData, clientInfo.firstName || '', lang, t);
  if (review) vm.review = review;
  const brand = resolveBrand(config, lang);
  const copy = getEmailCopy(lang, brand.informal);
  const htmlBody = review ? buildSaaReviewEmailHtml(vm, brand) : buildSaaConfirmationEmailHtml(vm, brand);

  const generalTerms = await buildGeneralTermsAttachment(config, lang);

  const quoteId = toGuidOrUndefined(sessionStorage.getItem('quote_id'));
  // The requestId parameter may carry EndPageScreen's relation-id fallback; only the real
  // submitted request id may be labelled RequestId (the relation already travels as
  // relationId) or drive the Aanvraag-PDF flow below. customerId keeps the fallback on
  // purpose - EndPageScreen documents "request_id if available, otherwise relation_id".
  const submittedRequestId = toGuidOrUndefined(sessionStorage.getItem('request_id'));
  const payload = {
    recipientEmail: emailData.customerEmail,
    bccEmail: resolveBcc(config),
    subject: review ? copy.review.subject : copy.subject,
    language: lang,
    htmlBody,
    firstName: clientInfo.initials,
    lastName: clientInfo.lastName,
    customerId: requestId,
    customerCode: (config.customer as any)?.emailCustomerCode || undefined,
    attachments: generalTerms ? [generalTerms] : undefined,
    relationId: toGuidOrUndefined(sessionStorage.getItem('vp_idnr')),
    calculationId: toGuidOrUndefined(sessionStorage.getItem('calculation_id')),
    quoteId,
    customMetadata: submittedRequestId ? { RequestId: submittedRequestId } : undefined,
  };

  // Confirmation mail: the BFF holds the mail until the Aanvraag PDF of the submitted
  // application is rendered and attaches it (202 + background worker), so the customer can
  // close the page right away. The customer should receive the application document, not the
  // offer, so this replaced the earlier quote-PDF flow. Without a submitted request id this
  // degrades to the direct send (mail without PDF beats no mail). The review mail stays on
  // the direct path on purpose: that aanvraag is under manual review and gets no document.
  if (!review && submittedRequestId) {
    return sendEmailDeferred({
      ...payload,
      requestId: submittedRequestId,
      pdfTemplateCode: SAA_APPLICATION_PDF_TEMPLATE,
      pdfFileName: saaApplicationPdfFileName(lang),
    });
  }

  return sendEmail(payload);
}
